import React, { useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useAuthState } from '../../context/UserAuthentication';
import styleClasses from '../../containers/account/accounts.module.scss';
import { Field, Formik } from 'formik';
import { FormField } from '../../components/FormFields/FormField';
import { IPersonalInfoFormFields } from '../../models/forms.model';
import { personalInfoSchema } from '../../validationSchemas/personalInfoSchema';
import { Toast_Func } from '../../helpers/toast.helper';
import useUpdateProfile from '../../react-query-hooks/useUpdateProfile';
import { useQueryClient } from 'react-query';
import { API_ERROR_MESSAGE } from '../../models/error.model';
import useMarketLocations from '../../react-query-hooks/useLocations';
import MaskedInput from 'react-text-mask';
import { CURRENT_USER } from '../../constants';
import { getUser } from '../../helpers/helperMethods';
import Button from '../Buttons/Button';
import InputMask from 'react-input-mask';
import SmsCheckbox from '../Forms/SmsCheckbox';
import { brazeLogEventSubscribedSMSMarketing } from '../../helpers/brazeHelper';
import { countDigits, handleBirthdayChange } from '../utils';

interface IPersonalInfoTabProps {
  eventKey: string;
  showEditInfoModal: () => void;
  data: any;
}
export const PHONE_NUMBER_MASK = [
  '(',
  /[2-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];
const PersonalInfoTab: React.FunctionComponent<IPersonalInfoTabProps> = ({
  data,
}) => {
  // const [value, setValue] = useState('');
  // const [isValid, setIsValid] = useState(false);
  const { authInfo } = useAuthState();
  const { mutateAsync } = useUpdateProfile();
  const queryClient = useQueryClient();
  const { data: markets } = useMarketLocations();
  const locations = markets?.map((market) => ({
    value: market.name,
    label: market.name,
    id: market.id,
  }));

  const birthdayRef = useRef(null);

  const [isBirthdayFieldTouched, setIsBirthdayFieldTouched] =
    useState<boolean>(false); // Local touched state

  const [isMessageSubscription, setIsMessageSubscription] =
    useState<boolean>(true);
  const [month, day, year] = (data?.birthday ?? '').split('-');
  const isOnlyYearMissing = !!month && !!day && !year;
  const isBirthdaySelected: boolean = !!month && !!day && !!year;
  const formattedDate = `${month ?? ''}/${day ?? ''}/${year ?? ''}`;
  const initialFormState: IPersonalInfoFormFields = {
    first_name: data ? data.first_name : '',
    last_name: data?.last_name || '',
    email: data?.email || '',
    phone: data?.phone || '',
    birthday: isOnlyYearMissing
      ? formattedDate
      : data?.birthday?.replace(/-/g, '/') || '',
    favorite_location: data?.locations?.map((loc) => {
      return {
        label: loc.name,
        value: loc.name,
        id: loc.id,
      };
    }),
  };
  const currentUserInfo = getUser();
  const handleFormSubmission = async (
    values: IPersonalInfoFormFields,
    { setSubmitting }: any
  ) => {
    const fvrtLocations = values.favorite_location?.map((loc) => loc.id);
    const finalValues: any = {
      id: authInfo.userId,
      ...(values.first_name != initialFormState.first_name && {
        first_name: values.first_name,
      }),
      ...(values.last_name != initialFormState.last_name && {
        last_name: values.last_name,
      }),
      ...(values.email != initialFormState.email && { email: values.email }),
      ...(values.phone != initialFormState.phone && { phone: values.phone }),
      ...(values.birthday != initialFormState.birthday && {
        birthday: values.birthday?.replace(/\//g, '-'),
      }),
      ...(values.favorite_location != initialFormState.favorite_location && {
        location: fvrtLocations ?? [],
      }),
      subscribe_to_sms_marketing: isMessageSubscription,
    };
    finalValues.first_name = finalValues?.first_name?.trim();
    finalValues.last_name = finalValues?.last_name?.trim();
    setSubmitting(true);
    return mutateAsync(
      {
        editedProfile: finalValues,
        authentication_token: authInfo.authentication_token,
      },
      {
        onSuccess: (data: any) => {
          queryClient.invalidateQueries('profile');
          Toast_Func({
            status: true,
            message: 'Profile successfully updated.',
          });
          if (
            isMessageSubscription &&
            !currentUserInfo?.subscribe_to_sms_marketing
          ) {
            if (values?.phone?.length) {
              brazeLogEventSubscribedSMSMarketing(data);
            }
          }
          localStorage.setItem(
            CURRENT_USER,
            JSON.stringify({
              ...currentUserInfo,
              ...finalValues,
              full_name: `${data?.first_name} ${data?.last_name}`,
              first_name: `${data?.first_name}`,
              last_name: `${data?.last_name}`,
            })
          );
        },
        onError: (error, variables, context) => {
          const {
            data: { message },
          } = (error as API_ERROR_MESSAGE)?.response;
          Toast_Func({ status: false, message: message || 'Something went wrong' });
        },
      }
    );
  };
  const handleMaskedValueChange = (newState: any, oldState: any) => {
    if (isOnlyYearMissing) {
      const fixedMonthDay = oldState.value.slice(0, 6); // MM/DD/

      // Keep what user typed in the year area
      const updatedYear = newState.value.slice(6);
      const newValue = `${fixedMonthDay}${updatedYear}`;

      // Let the user control the cursor within the year area
      const cursorPos = newState.selection?.start ?? 10;

      // Ensure cursor stays within year part
      const safeCursorPos = Math.max(6, Math.min(10, cursorPos));

      return {
        value: newValue,
        selection: {
          start: safeCursorPos,
          end: safeCursorPos,
        },
      };
    }

    return newState;
  };

  return (
    <div className={`${styleClasses.user_info_wrap} text-start w-100`}>
      <div className="mb-0 ">
        <h4 className="font-Cls f-sm-s22 f-s24 text-start h-24 medium_text mb-4">
          Profile & Preferences
        </h4>
      </div>
      <div className="new_form_design gap-40">
        <Formik
          initialValues={initialFormState}
          validationSchema={personalInfoSchema}
          onSubmit={handleFormSubmission}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            handleSubmit,
            isSubmitting,
            submitForm,
          }) => (
            <form
              className="new_form_design profile-info-read-only-field"
              onSubmit={handleSubmit}
            >
              <Row>
                <Col md={6} sm={12}>
                  <FormField
                    labelText={'FIRST NAME'}
                    name="first_name"
                    placeholder="Enter your first name"
                    value={values.first_name}
                    type="text"
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    readonly={isSubmitting}
                  />
                </Col>
                <Col md={6} sm={12}>
                  <FormField
                    labelText={'LAST NAME'}
                    placeholder="Enter your last name"
                    name="last_name"
                    value={values.last_name}
                    type="text"
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    readonly={isSubmitting}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6} sm={12}>
                  <FormField
                    labelText={'EMAIL ADDRESS'}
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={values.email}
                    onChange={null}
                    errors={errors}
                    touched={touched}
                    readonly
                    inputDisable={true}
                  />
                </Col>
                <Col sm={12} md={6} className="mb-3 mb-md-0">
                  <label htmlFor="phone">Phone Number</label>
                  <Field
                    name="phone"
                    render={({ field }) => (
                      <MaskedInput
                        {...field}
                        mask={PHONE_NUMBER_MASK}
                        id="phone"
                        placeHolder="Add a phone number for order alerts"
                        type="text"
                        value={values.phone.replace(/[() -]/g, '')}
                        onChange={(e) =>
                          setFieldValue(
                            'phone',
                            e.target.value.replace(/[() -]/g, '')
                          )
                        }
                        //setFieldValue('phone',value.replace(/[() -]/g,''))}
                        guide={true}
                        className={`
                          ${
                            errors.phone && touched.phone
                              ? 'input_error'
                              : 'text-input'
                          }
                            form-control`}
                      />
                    )}
                  />

                  {errors.phone && touched.phone && (
                    <div className="input-feedback clr-dark-red f-s14 font-rale mt-1 d-inline-block">
                      {errors.phone}
                    </div>
                  )}
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={6}>
                  <div className="form-group">
                    <label className="d-flex justify-content-between">
                      Birthday (MM/DD/YYYY)
                    </label>

                    <Field
                      name="birthday"
                      render={({ field }) => (
                        <InputMask
                          mask="99/99/9999"
                          maskPlaceholder="MM/DD/YYYY"
                          id="birthday"
                          {...field}
                          value={values.birthday}
                          readOnly={isBirthdaySelected}
                          beforeMaskedValueChange={handleMaskedValueChange}
                          onBlur={(e) => {
                            if (countDigits(values.birthday) < 1) {
                              setFieldValue('birthday', '');
                            }
                          }}
                          onChange={(e) =>
                            handleBirthdayChange(e, {
                              isBirthdayFieldTouched,
                              setIsBirthdayFieldTouched,
                              setFieldValue,
                              birthdayRef,
                            })
                          }
                          style={
                            isBirthdaySelected
                              ? {
                                  backgroundColor: '#e8e8e8',
                                  cursor: 'not-allowed',
                                }
                              : {}
                          }
                        >
                          {({ inputRef, ...rest }) => (
                            <input
                              {...rest}
                              ref={(el) => {
                                birthdayRef.current = el;
                                if (inputRef) {
                                  if (typeof inputRef === 'function')
                                    inputRef(el);
                                  else inputRef.current = el;
                                }
                              }}
                              readOnly={isBirthdaySelected}
                              className={`${
                                errors.birthday &&
                                touched.birthday &&
                                !isBirthdaySelected
                                  ? 'input_error'
                                  : 'text-input'
                              } form-control ${
                                isBirthdaySelected && 'inputDisable'
                              }`}
                              placeholder="Get a free treat on your birthday"
                            />
                          )}
                        </InputMask>
                      )}
                    />

                    {((errors.birthday && touched.birthday) ||
                      isBirthdayFieldTouched) &&
                      !isBirthdaySelected && (
                        <div className="input-feedback clr-dark-red f-s14 font-rale mt-1 d-inline-block">
                          {errors.birthday}
                        </div>
                      )}
                  </div>
                </Col>
                <Col sm={12} md={6}>
                  <div className="form-group custom_select">
                    <FormField
                      options={locations}
                      labelText={'favorite locations'}
                      name="favorite_location"
                      type={'multiselect'}
                      placeholder={'Select one for quick ordering'}
                      errors={errors}
                      touched={touched}
                      isSearchable={true}
                      value={values.favorite_location}
                      onChange={(location) => {
                        setFieldValue('favorite_location', location);
                      }}
                      inputFieldClass={'custom_select mb-0'}
                    />
                  </div>
                </Col>
              </Row>
              <SmsCheckbox
                setIsMessageSubscription={setIsMessageSubscription}
              />

              <hr className="custom_hr_sty mb-4 mt-5 d-md-none" />
              <div className="d-flex justify-content-center mt-4">
                <Button
                  type="button"
                  className="btn btn-large py-2 f-s16"
                  onClick={submitForm}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  SAVE PROFILE UPDATE
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
      <hr className="custom_hr_sty mb-4 mt-5 d-none d-md-block" />
    </div>
  );
};

export default PersonalInfoTab;
